import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  searchCheckinCustomers,
  type CheckinCustomerSearchResult,
} from "@/libs/checkinApi";

export type SelectedCustomer = {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  association?: CheckinCustomerSearchResult["association"];
  identitySuffix?: string;
};

type ActiveCustomerSearch = (
  facilityId: string,
  query: string,
  limit: number,
  cursor?: string,
) => Promise<{
  items: CheckinCustomerSearchResult[];
  nextCursor: string | null;
}>;

type CustomerPickerProps = {
  facilityId: string;
  facilityName: string;
  value: SelectedCustomer | null;
  onChange: (customer: SelectedCustomer | null) => void;
  disabled?: boolean;
} & (
  | {
      activeSessionsOnly: true;
      searchActiveCustomers: ActiveCustomerSearch;
    }
  | {
      activeSessionsOnly?: false;
      searchActiveCustomers?: never;
    }
);

const ACTIVE_CUSTOMER_SEARCH_LIMIT = 10;
const ACTIVE_CUSTOMER_SEARCH_MAX_LENGTH = 100;

const associationLabels: Record<
  CheckinCustomerSearchResult["association"],
  string
> = {
  booking: "Booking customer",
  checkin: "Previous check-in",
  booking_and_checkin: "Booking and check-in customer",
  customer: "Facility customer",
  party: "Active party",
  exact_id: "Exact customer ID",
};

export function customerIdentitySuffix(userId: string): string {
  return userId.slice(-6).toUpperCase();
}

function safeDisplayName(
  displayName: string | null | undefined,
  userId: string,
): string | null {
  const resolved = displayName?.trim();
  if (!resolved || resolved === userId.trim()) return null;
  return resolved;
}

function associationLabel(
  association: SelectedCustomer["association"],
): string {
  return association ? associationLabels[association] : "Customer";
}

function boundSearchQuery(value: string, maxLength: number | null): string {
  return maxLength === null || value.length <= maxLength
    ? value
    : value.trim().slice(0, maxLength);
}

export function CustomerPicker({
  facilityId,
  facilityName,
  value,
  onChange,
  disabled = false,
  activeSessionsOnly = false,
  searchActiveCustomers,
}: CustomerPickerProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CheckinCustomerSearchResult[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [searchAttempt, setSearchAttempt] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchGenerationRef = useRef(0);
  const activeSearch = activeSessionsOnly ? searchActiveCustomers : undefined;
  const trimmedQuery = query.trim();
  const maxQueryLength = activeSessionsOnly
    ? ACTIVE_CUSTOMER_SEARCH_MAX_LENGTH
    : null;

  useEffect(() => {
    let cancelled = false;
    const searchGeneration = ++searchGenerationRef.current;
    const isCurrentSearch = () =>
      !cancelled && searchGenerationRef.current === searchGeneration;

    if (value || disabled) {
      setResults([]);
      setNextCursor(null);
      setSearching(false);
      setLoadingMore(false);
      setLoadMoreFailed(false);
      return;
    }
    const trimmed = trimmedQuery;
    if (trimmed.length < 2) {
      setResults([]);
      setNextCursor(null);
      setSearching(false);
      setSearchFailed(false);
      setLoadingMore(false);
      setLoadMoreFailed(false);
      return;
    }

    setResults([]);
    setNextCursor(null);
    setSearching(true);
    setSearchFailed(false);
    setLoadingMore(false);
    setLoadMoreFailed(false);
    const timeout = window.setTimeout(() => {
      const search = activeSearch
        ? activeSearch(facilityId, trimmed, ACTIVE_CUSTOMER_SEARCH_LIMIT)
        : searchCheckinCustomers(facilityId, trimmed).then((items) => ({
            items,
            nextCursor: null,
          }));
      search
        .then((page) => {
          if (isCurrentSearch()) {
            setResults(page.items);
            setNextCursor(page.nextCursor);
          }
        })
        .catch(() => {
          if (isCurrentSearch()) {
            setResults([]);
            setNextCursor(null);
            setSearchFailed(true);
          }
        })
        .finally(() => {
          if (isCurrentSearch()) setSearching(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    activeSearch,
    disabled,
    facilityId,
    query,
    searchAttempt,
    trimmedQuery,
    value,
  ]);

  async function loadMoreCustomers() {
    if (!activeSearch || !nextCursor || searching || loadingMore || disabled) {
      return;
    }
    const searchGeneration = searchGenerationRef.current;
    const cursor = nextCursor;
    const queryForPage = trimmedQuery;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    try {
      const page = await activeSearch(
        facilityId,
        queryForPage,
        ACTIVE_CUSTOMER_SEARCH_LIMIT,
        cursor,
      );
      if (searchGenerationRef.current !== searchGeneration) return;
      setResults((current) => {
        const seen = new Set(current.map((player) => player.user_id));
        return [
          ...current,
          ...page.items.filter((player) => !seen.has(player.user_id)),
        ];
      });
      setNextCursor(page.nextCursor);
    } catch {
      if (searchGenerationRef.current === searchGeneration) {
        setLoadMoreFailed(true);
      }
    } finally {
      if (searchGenerationRef.current === searchGeneration) {
        setLoadingMore(false);
      }
    }
  }

  const resolvedResults = useMemo(
    () =>
      results.flatMap((player) => {
        const displayName = safeDisplayName(
          player.display_name,
          player.user_id,
        );
        return displayName ? [{ ...player, displayName }] : [];
      }),
    [results],
  );
  if (value) {
    const context = activeSessionsOnly
      ? "Checked in now"
      : associationLabel(value.association);
    const displayName =
      safeDisplayName(value.displayName, value.userId) ?? "Customer";
    const suffix = customerIdentitySuffix(value.userId);
    const suffixCopy = suffix ? ` · ID ending ${suffix}` : "";
    return (
      <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar
            src={value.avatarUrl ?? undefined}
            alt=""
            sx={{ flexShrink: 0 }}
          >
            {displayName.charAt(0)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {context} at {facilityName}
              {suffixCopy}
            </Typography>
          </Box>
          <IconButton
            type="button"
            aria-label={`Change customer from ${displayName}${
              suffix ? `, ID ending ${suffix}` : ""
            }`}
            title="Change customer"
            disabled={disabled}
            sx={{ "&.Mui-disabled": { pointerEvents: "auto" } }}
            onClick={() => {
              setQuery("");
              onChange(null);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={1} sx={{ minWidth: 0, maxWidth: "100%" }}>
      <TextField
        id="customer-search"
        type="search"
        label="Customer"
        placeholder={
          activeSessionsOnly
            ? "Checked-in customer name"
            : "Name or exact customer ID"
        }
        inputRef={searchInputRef}
        value={query}
        disabled={disabled}
        fullWidth
        slotProps={{
          htmlInput: {
            "aria-label": activeSessionsOnly
              ? "Search checked-in customers"
              : "Search customers",
          },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
          },
        }}
        onChange={(event) => {
          const nextQuery = boundSearchQuery(
            event.target.value,
            maxQueryLength,
          );
          if (nextQuery === query) return;
          searchGenerationRef.current += 1;
          setQuery(nextQuery);
          setResults([]);
          setNextCursor(null);
          setSearchFailed(false);
          setLoadingMore(false);
          setLoadMoreFailed(false);
        }}
      />

      {searching ? (
        <Typography variant="caption" color="text.secondary" role="status">
          Searching...
        </Typography>
      ) : null}

      {searchFailed ? (
        <Alert
          severity="error"
          action={
            <Button
              type="button"
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              aria-label="Retry customer search"
              disabled={disabled}
              onClick={() => {
                setSearchAttempt((attempt) => attempt + 1);
                searchInputRef.current?.focus();
              }}
            >
              Retry
            </Button>
          }
        >
          Customer search unavailable.
        </Alert>
      ) : null}

      {!searching &&
      query.trim().length >= 2 &&
      resolvedResults.length === 0 &&
      !nextCursor &&
      !searchFailed ? (
        <Typography variant="caption" color="text.secondary" role="status">
          {activeSessionsOnly
            ? "No checked-in customers found."
            : "No customers found."}
        </Typography>
      ) : null}

      {resolvedResults.length > 0 || nextCursor ? (
        <Paper
          variant="outlined"
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            maxHeight: 224,
            overflow: "auto",
          }}
        >
          <List disablePadding>
            {resolvedResults.map((player) => {
              const context = activeSessionsOnly
                ? "Checked in now"
                : associationLabel(player.association);
              const suffix = customerIdentitySuffix(player.user_id);
              return (
                <ListItemButton
                  key={player.user_id}
                  aria-label={`Select ${player.displayName}, ${context} at ${facilityName}${
                    suffix ? `, customer ID ending ${suffix}` : ""
                  }`}
                  onClick={() => {
                    searchGenerationRef.current += 1;
                    onChange({
                      userId: player.user_id,
                      displayName: player.displayName,
                      avatarUrl: player.avatar_url,
                      association: player.association,
                      identitySuffix: suffix,
                    });
                    setQuery("");
                    setResults([]);
                    setNextCursor(null);
                    setLoadMoreFailed(false);
                  }}
                  sx={{ minWidth: 0 }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar src={player.avatar_url ?? undefined} alt="">
                      {player.displayName.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ minWidth: 0 }}
                    primary={player.displayName}
                    secondary={
                      <>
                        {context} at {facilityName}
                        {suffix ? ` · ID ending ${suffix}` : ""}
                      </>
                    }
                    slotProps={{
                      primary: { noWrap: true, sx: { fontWeight: 600 } },
                      secondary: { noWrap: true },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
          {nextCursor ? (
            <Stack
              spacing={0.5}
              sx={{ borderTop: 1, borderColor: "divider", p: 1 }}
            >
              {loadMoreFailed ? (
                <Typography variant="caption" color="error" role="alert">
                  More customers unavailable.
                </Typography>
              ) : null}
              <Button
                type="button"
                size="small"
                fullWidth
                disabled={disabled || loadingMore}
                onClick={() => void loadMoreCustomers()}
              >
                {loadingMore
                  ? "Loading more..."
                  : loadMoreFailed
                    ? "Retry more customers"
                    : "Load more customers"}
              </Button>
            </Stack>
          ) : null}
        </Paper>
      ) : null}
    </Stack>
  );
}
